<template>
  <div class="max-w-4xl mx-auto px-4">
    <!-- 標題區塊 -->
    <div class="mb-8 text-center">
      <h1 class="text-3xl font-bold text-wood-800 mb-4">
        <i class="fas fa-book-open mr-3"></i>
        太極拳教學教材
      </h1>
      <p class="text-wood-600 text-lg">
        精選太極拳教學內容，幫助您深入了解太極拳的精髓 （本頁面內容正在持續更新中）
      </p>
    </div>

    <!-- 教材內容區塊 -->
    <div class="space-y-8">

      <!-- 基礎教材 -->
      <section class="bg-white/80 backdrop-blur-sm rounded-2xl p-8 post-shadow">
        <h2 class="text-2xl font-semibold text-wood-800 mb-6 flex items-center">
          <i class="fas fa-seedling mr-3 text-wood-600"></i>
          基礎入門
        </h2>
        <div class="grid md:grid-cols-2 gap-6">


          <!-- 太極拳基本概念 -->
          <div class="bg-wood-50 rounded-xl p-6 border border-wood-200">
            <h3 class="text-xl font-medium text-wood-800 mb-3">太極拳基本概念</h3>
            <p class="text-wood-700 mb-4">了解太極拳的起源、哲學思想與基本原理</p>
            <div class="text-wood-600 text-sm">
              <i class="fas fa-clock mr-2"></i>預計學習時間：30分鐘
            </div>
          </div>

          <!-- 太極拳萬用播放器 -->
          <div class="bg-gradient-to-br from-amber-50 to-wood-100 rounded-xl p-6 border-2 border-wood-300 hover:border-wood-500 hover:shadow-xl transition-all duration-300 cursor-pointer group"
               @click="navigateToUniversalPlayer">
            <h3 class="text-xl font-bold text-wood-800 mb-3 flex items-center">
              <i class="fas fa-infinity mr-3 text-amber-600 animate-pulse-soft"></i>
              太極拳萬用播放器
            </h3>
            <p class="text-wood-700 mb-4">整合一般播放與練習模式，支援片段重複練習與語音提醒功能</p>
            <div class="flex items-center justify-between">
              <div class="text-wood-600 text-sm">
                <i class="fas fa-hand-pointer mr-2"></i>標籤式切換播放模式
              </div>
              <div class="text-wood-500 group-hover:text-wood-700 group-hover:translate-x-1 transition-all">
                <i class="fas fa-arrow-right text-xl"></i>
              </div>
            </div>
            <div class="mt-3 flex gap-2">
              <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                <i class="fas fa-play-circle mr-1"></i>一般播放
              </span>
              <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <i class="fas fa-dumbbell mr-1"></i>練習模式
              </span>
            </div>
          </div>


        </div>

        <!-- 第二行 - 楊家傳承譜系 -->
        <div
          class="bg-gradient-to-br from-amber-50 to-wood-100 rounded-xl p-6 border-2 border-wood-300 hover:border-wood-500 hover:shadow-xl transition-all duration-300 cursor-pointer group mt-6"
          @click="navigateToLineage"
        >
          <h3 class="text-xl font-medium text-wood-800 mb-3 flex items-center group-hover:text-wood-900">
            <i class="fas fa-sitemap mr-3 text-amber-600"></i>
            楊家傳承譜系
          </h3>
          <p class="text-wood-700 mb-4">楊氏太極拳歷代宗師傳承關係圖，互動式探索家族譜系</p>
          <div class="flex items-center justify-between">
            <div class="text-wood-600 text-sm">
              <i class="fas fa-hand-pointer mr-2"></i>點擊進入互動式查看
            </div>
            <i class="fas fa-arrow-right text-wood-500 group-hover:text-wood-700 group-hover:translate-x-1 transition-all"></i>
          </div>
        </div>
      </section>

      <!-- 拳譜資料 -->
      <section class="bg-white/80 backdrop-blur-sm rounded-2xl p-8 post-shadow">
        <h2 class="text-2xl font-semibold text-wood-800 mb-6 flex items-center">
          <i class="fas fa-book mr-3 text-wood-600"></i>
          拳譜資料
        </h2>

        <!-- 載入狀態 -->
        <div v-if="isLoadingBoxingCharts" class="flex justify-center items-center py-12">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-wood-600"></div>
          <span class="ml-3 text-wood-600">載入拳譜資料中...</span>
        </div>

        <!-- 拳譜列表 -->
        <div v-else-if="boxingCharts.length > 0" class="grid md:grid-cols-2 gap-6">
          <div
            v-for="chart in boxingCharts"
            :key="chart.id"
            class="bg-gradient-to-r from-wood-50 to-amber-50 rounded-xl p-6 border-2 border-wood-200 hover:shadow-xl transition-all duration-300 cursor-pointer group hover:border-wood-400 hover:transform hover:scale-[1.02]"
            @click="navigateToBoxingChart(chart.id)"
          >
            <!-- 拳譜標題 -->
            <div class="flex items-start justify-between mb-4">
              <div class="flex-1">
                <h3 class="text-xl font-bold text-wood-800 mb-2 group-hover:text-wood-900 transition-colors flex items-center">
                  <i class="fas fa-yin-yang mr-3 text-amber-600 animate-pulse-soft"></i>
                  {{ chart.name }}
                </h3>
                <div class="flex items-center space-x-2">

                  <span v-if="!chart.json_file_exists" class="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">
                    <i class="fas fa-exclamation-triangle"></i> 資料待補
                  </span>
                </div>
              </div>
              <div class="text-wood-400 group-hover:text-wood-600 transition-colors">
                <i class="fas fa-chevron-right text-2xl"></i>
              </div>
            </div>

            <!-- 拳譜描述 -->
            <p class="text-wood-700 text-sm leading-relaxed mb-4 line-clamp-3">
              {{ chart.description }}
            </p>

            <!-- 底部按鈕 -->
            <div class="flex items-center justify-end pt-4 border-t border-wood-200">
              <div class="text-wood-600 group-hover:text-wood-800 text-sm font-medium transition-colors flex items-center">
                <span>查看詳情</span>
                <i class="fas fa-arrow-right ml-2 group-hover:translate-x-1 transition-transform"></i>
              </div>
            </div>
          </div>
        </div>

        <!-- 無拳譜資料時的提示 -->
        <div v-else class="text-center py-12">
          <i class="fas fa-book-open text-wood-400 text-4xl mb-4"></i>
          <p class="text-wood-600 text-lg">目前還沒有拳譜資料</p>
          <p class="text-wood-500 text-sm mt-2">拳譜內容正在整理中，敬請期待</p>
        </div>
      </section>

      <!-- 進階練習 -->
      <section class="bg-white/80 backdrop-blur-sm rounded-2xl p-8 post-shadow">
        <h2 class="text-2xl font-semibold text-wood-800 mb-6 flex items-center">
          <i class="fas fa-mountain mr-3 text-wood-600"></i>
          進階練習
        </h2>
        <div class="grid md:grid-cols-2 gap-6">
          <div class="bg-wood-50 rounded-xl p-6 border border-wood-200">
            <h3 class="text-xl font-medium text-wood-800 mb-3">推手練習</h3>
            <p class="text-wood-700 mb-4">雙人練習，感受勁力的運用與化解</p>
            <div class="flex items-center justify-between">
              <div class="text-wood-600 text-sm">
                <i class="fas fa-users mr-2"></i>需要練習夥伴
              </div>
              <span class="bg-wood-600 text-white text-xs px-3 py-1 rounded-full">進階</span>
            </div>
          </div>
          <div class="bg-wood-50 rounded-xl p-6 border border-wood-200">
            <h3 class="text-xl font-medium text-wood-800 mb-3">太極劍</h3>
            <p class="text-wood-700 mb-4">太極拳的器械練習，提升身法與協調</p>
            <div class="flex items-center justify-between">
              <div class="text-wood-600 text-sm">
                <i class="fas fa-trowel mr-2"></i>需要器械
              </div>
              <span class="bg-wood-600 text-white text-xs px-3 py-1 rounded-full">進階</span>
            </div>
          </div>
        </div>
      </section>

      <!-- 文字教材 -->
      <section class="bg-white/80 backdrop-blur-sm rounded-2xl p-8 post-shadow">
        <h2 class="text-2xl font-semibold text-wood-800 mb-6 flex items-center">
          <i class="fas fa-scroll mr-3 text-wood-600"></i>
          精選文章
        </h2>

        <!-- 載入狀態 -->
        <div v-if="isLoadingPosts" class="flex justify-center items-center py-12">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-wood-600"></div>
          <span class="ml-3 text-wood-600">載入精選文章中...</span>
        </div>

        <!-- 精選文章列表 -->
        <div v-else-if="featuredPosts.length > 0" class="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div
            v-for="post in featuredPosts"
            :key="post.id"
            class="bg-white rounded-xl border border-gray-200 hover:shadow-lg transition-all duration-200 cursor-pointer group overflow-hidden"
            @click="navigateToPost(post.id)"
          >
            <!-- Card Header - 文章標題 -->
            <div class="border-b border-gray-200 bg-gray-50 px-4 py-3">
              <h3 class="text-base font-semibold text-gray-900 group-hover:text-wood-700 transition-colors line-clamp-2">
                {{ getFirstLine(post.short) }}
              </h3>
            </div>

            <!-- Card Body - 文章摘要 -->
            <div class="px-4 py-4">
              <div class="text-gray-700 text-sm leading-relaxed line-clamp-4 mb-4">
                {{ getRemainingContent(post.short) }}
              </div>

              <!-- 底部資訊 -->
              <div class="flex items-center justify-between pt-3 border-t border-gray-100">
                <div class="flex items-center text-gray-500 text-xs space-x-3">
                  <span><i class="fas fa-heart mr-1"></i>{{ post.likes }}</span>
                  <span><i class="fas fa-comment mr-1"></i>{{ post.comments }}</span>
                </div>
                <div class="text-wood-600 group-hover:text-wood-800 text-xs font-medium transition-colors">
                  閱讀全文 <i class="fas fa-arrow-right ml-1"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 無精選文章時的提示 -->
        <div v-else class="text-center py-12">
          <i class="fas fa-file-alt text-wood-400 text-4xl mb-4"></i>
          <p class="text-wood-600 text-lg">目前還沒有精選文章</p>
          <p class="text-wood-500 text-sm mt-2">管理員會定期精選優質內容與大家分享</p>
        </div>
      </section>

      <!-- 理論知識 -->
      <section class="bg-white/80 backdrop-blur-sm rounded-2xl p-8 post-shadow">
        <h2 class="text-2xl font-semibold text-wood-800 mb-6 flex items-center">
          <i class="fas fa-yin-yang mr-3 text-wood-600"></i>
          理論知識
        </h2>
        <div class="space-y-4">
          <div class="border-l-4 border-wood-500 pl-6 py-4">
            <h3 class="text-lg font-medium text-wood-800 mb-2">陰陽學說</h3>
            <p class="text-wood-700">太極拳的哲學基礎，了解陰陽互換的原理</p>
          </div>
          <div class="border-l-4 border-wood-500 pl-6 py-4">
            <h3 class="text-lg font-medium text-wood-800 mb-2">鬆柔沉靜</h3>
            <p class="text-wood-700">為現代人急躁忙碌的生活環境，注入一股清泉，紓解壓力，緩解慢性疾病</p>
          </div>
          <div class="border-l-4 border-wood-500 pl-6 py-4">
            <h3 class="text-lg font-medium text-wood-800 mb-2">經絡養生</h3>
            <p class="text-wood-700">太極拳對人體經絡的影響與健康效益</p>
          </div>
        </div>
      </section>

    </div>

    <!-- 提示文字 -->
    <div class="mt-12 text-center">
      <div class="bg-gradient-to-r from-wood-100 to-wood-200 rounded-2xl p-6">
        <i class="fas fa-info-circle text-wood-600 text-2xl mb-4"></i>
        <p class="text-wood-700">
          更多教學內容正在整理中，敬請期待...
        </p>
        <p class="text-wood-600 text-sm mt-2">
          如有任何問題，歡迎在社群中與我們交流討論
        </p>
      </div>
    </div>

    <!-- 太極拳論模態框 -->
    <div v-if="showTaijiTheoryModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" @click="showTaijiTheoryModal = false">
      <div class="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl" @click.stop>
        <!-- 標題列 -->
        <div class="bg-gradient-to-r from-wood-600 to-wood-700 text-white p-6 flex items-center justify-between">
          <h2 class="text-2xl font-bold">太極拳論　山右王宗岳</h2>
          <button @click="showTaijiTheoryModal = false" class="text-white hover:text-wood-200 text-2xl">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- 內容區域 -->
        <div class="p-8 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div class="text-wood-800 leading-relaxed space-y-4">
            <div class="text-center font-bold text-xl mb-6 text-wood-900">太極拳論</div>

            <p>太極者，無極而生。動靜之機，陰陽之母也。</p>
            <p>動之則分，靜之則合。無過不及，隨曲就伸。</p>
            <p>人剛我柔謂之走，我順人背謂之粘；動急則急應，動緩則緩隨。雖變化萬端，而理惟一貫。</p>
            <p>由著熟而漸悟懂劤，由懂劤而階及神明。然非用力之久，不能豁然貫通焉。</p>
            <p>虛領頂勁，氣沉丹田。不偏不倚，忽隱忽現。左重則左虛，右沉則右杳。</p>
            <p>仰之則彌高，俯之則彌深。進之則愈長，退之則愈促。一羽不能加，蠅蟲不能落。人不知我，我獨知人。</p>
            <p>英雄所向無敵，蓋皆由此而及也。</p>
            <p>斯技旁門甚多，雖勢有區別，概不外乎壯欺弱、慢讓快耳。有力打無力，手慢讓手快，是皆先天自然之能，非關學力而有為也。察「四兩撥千斤」之句，顯非力勝；觀耄耋能禦眾之形，快何能為？</p>
            <p>立如平準，活似車輪，偏沉則隨，雙重則滯。每見數年純功，不能運化者，率皆自為人制，雙重之病未悟耳。</p>
            <p>欲避此病，須知陰陽。粘即是走，走即是粘，陰不離陽，陽不離陰，陰陽相濟，方為懂勁。</p>
            <p>懂劤後，愈練愈精，默識揣摩，漸至從心所欲。</p>
            <p>本是捨己從人，多誤捨近求遠，所謂「差之毫厘，謬之千里」，學者不可不詳辨焉。</p>
            <p class="text-sm text-wood-600 border-t border-wood-200 pt-4 mt-6">
              註云：此論句句切要在心，並無一字陪襯，非有夙慧，不能悟也。先師不肯妄傳，非獨擇人，亦恐枉費工夫耳。
            </p>

            <div class="mt-8 border-t border-wood-200 pt-6">
              <div class="text-center font-bold text-xl mb-4 text-wood-900">太極拳經</div>

              <p>一舉動周身俱要輕靈，尤須貫串。氣宜鼓盪，神宜內斂，無使有缺陷處，無使有凹凸處，無使有斷續處。其根在腳，發於腿，主宰於腰，行於手指，由腳而腿而腰，總須完整一氣，向前退後，乃能得機得勢。有不得機得勢處，身便散亂，其病必於腰腿求之，上下前後左右皆然。凡此皆是意，不在外面，有上即有下，有前則有後，有左則有右。如意要向上，即寓下意，若將物掀起而加以挫之之力。斯其根自斷，乃攘之速而無疑。虛實宜分清楚，一處有一處虛實，處處總此一虛實，周身節節貫串，無令絲毫間斷耳。</p>

              <p>長拳者，如長江大海，滔滔不絕也。掤、履、擠、按、採、挒、肘、靠，此八卦也。進步、退步、左顧、右盼、中定，此五行也。掤、履、擠、按，即乾、坤、坎、離、四正方也。採、挒、肘、靠，即巽、震、兌、艮、四斜角也。進退顧盼定，即金木水火土也，合之則為十三勢也。</p>

              <p class="text-sm text-wood-600 mt-4">
                原註云：此係武當山張三丰祖師遺論。欲天下豪傑延年益壽，不徒作技藝之末也。
              </p>
            </div>
          </div>
        </div>

        <!-- 底部按鈕 -->
        <div class="bg-wood-50 p-4 text-center border-t border-wood-200">
          <button @click="showTaijiTheoryModal = false"
                  class="px-6 py-2 bg-wood-600 text-white rounded-lg hover:bg-wood-700 transition-colors">
            關閉
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import axios from 'axios'

// 響應式變數
const showTaijiTheoryModal = ref(false)
const featuredPosts = ref([])
const isLoadingPosts = ref(false)
const boxingCharts = ref([])
const isLoadingBoxingCharts = ref(false)

// 獲取拳譜資料
const fetchBoxingCharts = async () => {
  isLoadingBoxingCharts.value = true
  try {
    const response = await axios.get('/api/v1/boxing_charts')
    boxingCharts.value = response.data
    console.log('拳譜資料載入成功:', boxingCharts.value)
  } catch (error) {
    console.error('無法獲取拳譜資料:', error)
  } finally {
    isLoadingBoxingCharts.value = false
  }
}

// 獲取精選文章
const fetchFeaturedPosts = async () => {
  isLoadingPosts.value = true
  try {
    const response = await axios.get('/posts.json?featured=true')
    featuredPosts.value = response.data.slice(0, 20) // 最多顯示12篇
  } catch (error) {
    console.error('無法獲取精選文章:', error)
  } finally {
    isLoadingPosts.value = false
  }
}

// 導航到拳譜詳情頁
const navigateToBoxingChart = (chartId) => {
  window.location.href = `/boxing_charts/${chartId}`
}

// 導航到萬用播放器頁面
const navigateToUniversalPlayer = () => {
  window.location.href = '/universal_player'
}

// 導航到傳承譜系頁面
const navigateToLineage = () => {
  window.history.pushState(null, '', '/lineage')
  window.location.href = '/lineage'
}

// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('zh-TW', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

// 導航到文章詳情頁
const navigateToPost = (postId) => {
  // 使用 Vue 路由方式導航
  window.history.pushState(null, '', `/posts/${postId}`)
  window.location.href = `/posts/${postId}`
}

// 從 HTML 內容中提取第一行作為標題
const getFirstLine = (htmlContent) => {
  if (!htmlContent) return '無標題'

  // 移除 HTML 標籤，取得純文字
  const textContent = htmlContent.replace(/<[^>]*>/g, '').trim()

  // 依據換行符號或句號分割，取第一行
  const firstLine = textContent.split(/[\n\r。]/)[0].trim()

  // 如果第一行太長，截取前50個字元
  return firstLine.length > 50 ? firstLine.substring(0, 50) + '...' : firstLine || '無標題'
}

// 取得除第一行外的其餘內容作為摘要
const getRemainingContent = (htmlContent) => {
  if (!htmlContent) return ''

  // 移除 HTML 標籤，取得純文字
  const textContent = htmlContent.replace(/<[^>]*>/g, '').trim()

  // 依據換行符號或句號分割
  const lines = textContent.split(/[\n\r。]/)

  // 取得除第一行外的內容
  if (lines.length > 1) {
    const remainingContent = lines.slice(1).join('').trim() // 可以把換行字元換成...
    // 限制摘要長度，避免太長
    return remainingContent.length > 150 ? remainingContent.substring(0, 150) + '...' : remainingContent
  }

  return ''
}

// 組件掛載時的初始化
onMounted(() => {
  console.log('教材頁面已載入')
  fetchBoxingCharts()
  fetchFeaturedPosts()
})
</script>

<style scoped>
/* 繼承全域樣式，這裡可以添加教材頁面特定的樣式 */

/* 文字截斷樣式 */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-4 {
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* 太極圖動畫 */
@keyframes pulse-soft {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
}

.animate-pulse-soft {
  animation: pulse-soft 3s ease-in-out infinite;
}
</style>